/**
 * Definição de mundos e fases (CLAUDE.md §5).
 *
 * Os 6 mundos da v1 estão totalmente especificados — 54 fases no total,
 * cobrindo tabuadas de 1 a 12 com curva progressiva de dificuldade.
 *
 * Cada fase carrega seu próprio pool de tabuadas, tempo por conta e
 * número de perguntas, permitindo curva de dificuldade explícita dentro
 * do mundo (e independente do tier do jogador).
 */

export interface Level {
  /** Chave estável usada em saveData.starsPerLevel. */
  id: string;
  /** Número da fase dentro do mundo (1..N). Boss tem o último número. */
  number: number;
  name: string;
  /** Curiosidade/tema da fase, exibida na UI. */
  flavor: string;
  /** Pool de tabuadas usado nesta fase. */
  tables: number[];
  /** Tempo por conta em ms. */
  timeLimitMs: number;
  /** Quantas perguntas para concluir a fase. */
  questionCount: number;
  isBoss: boolean;
}

export interface World {
  id: number;
  name: string;
  theme: string;
  accentCss: string;
  /** Vazio = mundo placeholder (em breve). */
  levels: Level[];
}

export const WORLDS: readonly World[] = [
  {
    id: 1,
    name: 'Academia Geek',
    theme: 'Tutorial e fundamentos',
    accentCss: '#06b6d4',
    levels: [
      {
        id: 'world-1-level-1',
        number: 1,
        name: 'Hello World',
        flavor: 'Sua primeira linha de tabuada',
        tables: [1, 2],
        timeLimitMs: 9000,
        questionCount: 6,
        isBoss: false,
      },
      {
        id: 'world-1-level-2',
        number: 2,
        name: 'Variáveis',
        flavor: 'Guarde valores na memória',
        tables: [1, 2],
        timeLimitMs: 8000,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-1-level-3',
        number: 3,
        name: 'If / Else',
        flavor: 'Decida o caminho certo',
        tables: [1, 2, 5],
        timeLimitMs: 8000,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-1-level-4',
        number: 4,
        name: 'Loop For',
        flavor: 'Repita até dominar',
        tables: [2, 5],
        timeLimitMs: 7000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-1-level-5',
        number: 5,
        name: 'Funções',
        flavor: 'Reaproveite o que aprendeu',
        tables: [1, 2, 5, 10],
        timeLimitMs: 7000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-1-level-6',
        number: 6,
        name: 'Arrays',
        flavor: 'Várias respostas em sequência',
        tables: [2, 5, 10],
        timeLimitMs: 6000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-1-level-7',
        number: 7,
        name: 'Console.log',
        flavor: 'Mostre o que sabe',
        tables: [1, 2, 5, 10],
        timeLimitMs: 6000,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-1-level-8',
        number: 8,
        name: 'Debugger',
        flavor: 'Cace os erros',
        tables: [1, 2, 5, 10],
        timeLimitMs: 6000,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-1-level-boss',
        number: 9,
        name: 'Professor Glitch',
        flavor: 'O bug-chefe da Academia',
        tables: [1, 2, 5, 10],
        timeLimitMs: 5000,
        questionCount: 15,
        isBoss: true,
      },
    ],
  },
  {
    id: 2,
    name: 'Reino da Programação',
    theme: 'Algoritmos, recursão e refactor',
    accentCss: '#22c55e',
    levels: [
      {
        id: 'world-2-level-1',
        number: 1,
        name: 'Compilador',
        flavor: 'Aquece o motor — revisão do que já dominou',
        tables: [1, 2, 5, 10],
        timeLimitMs: 7000,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-2-level-2',
        number: 2,
        name: 'Recursão',
        flavor: 'A função que se chama: tabuada do 3 entra em cena',
        tables: [3],
        timeLimitMs: 7000,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-2-level-3',
        number: 3,
        name: 'Algoritmo',
        flavor: 'Misture o 3 com o que você já conhece',
        tables: [2, 3, 5],
        timeLimitMs: 6500,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-2-level-4',
        number: 4,
        name: 'Stack & Heap',
        flavor: 'Empilhe respostas — agora com tabuada do 4',
        tables: [3, 4],
        timeLimitMs: 6500,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-2-level-5',
        number: 5,
        name: 'Big O',
        flavor: 'Otimize o tempo de resposta',
        tables: [3, 4],
        timeLimitMs: 6000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-2-level-6',
        number: 6,
        name: 'Async / Await',
        flavor: 'Promessa de resultado — chega a tabuada do 6',
        tables: [4, 6],
        timeLimitMs: 6000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-2-level-7',
        number: 7,
        name: 'Refactor',
        flavor: 'Limpe o código, mantenha a precisão',
        tables: [3, 4, 6],
        timeLimitMs: 5500,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-2-level-8',
        number: 8,
        name: 'Pull Request',
        flavor: 'Revisão final antes do merge',
        tables: [3, 4, 5, 6, 10],
        timeLimitMs: 5000,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-2-level-boss',
        number: 9,
        name: 'Bug Final 404',
        flavor: 'O bug-chefe que insiste em não ser encontrado',
        tables: [2, 3, 4, 5, 6, 10],
        timeLimitMs: 4500,
        questionCount: 15,
        isBoss: true,
      },
    ],
  },
  {
    id: 3,
    name: 'Galáxia Sci-Fi',
    theme: 'Naves, alienígenas e o cosmos',
    accentCss: '#7c3aed',
    levels: [
      {
        id: 'world-3-level-1',
        number: 1,
        name: 'Lançamento',
        flavor: 'Aquecimento dos motores — revisa o que já dominou',
        tables: [3, 4, 5, 10],
        timeLimitMs: 6500,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-3-level-2',
        number: 2,
        name: 'Cinturão de Asteroides',
        flavor: 'Rotas perigosas — a tabuada do 7 entra em órbita',
        tables: [7],
        timeLimitMs: 6500,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-3-level-3',
        number: 3,
        name: 'Buraco Negro',
        flavor: 'Mistura o 7 com o que você já manda bem',
        tables: [3, 4, 7],
        timeLimitMs: 6000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-3-level-4',
        number: 4,
        name: 'Cometa Halley',
        flavor: 'Passa raro — agora com tabuada do 8',
        tables: [7, 8],
        timeLimitMs: 6000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-3-level-5',
        number: 5,
        name: 'Estrela Anã',
        flavor: 'Pequena no tamanho, grande na precisão',
        tables: [4, 7, 8],
        timeLimitMs: 5500,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-3-level-6',
        number: 6,
        name: 'Nebulosa',
        flavor: 'Nuvens cósmicas pedem foco — 6, 7 e 8 misturadas',
        tables: [6, 7, 8],
        timeLimitMs: 5500,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-3-level-7',
        number: 7,
        name: 'Buraco de Minhoca',
        flavor: 'Atalho cósmico — toda a tabela alta de uma vez',
        tables: [6, 7, 8, 10],
        timeLimitMs: 5000,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-3-level-8',
        number: 8,
        name: 'Velocidade da Luz',
        flavor: 'Reta final antes do imperador',
        tables: [3, 4, 6, 7, 8, 10],
        timeLimitMs: 4500,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-3-level-boss',
        number: 9,
        name: 'Imperador Binário',
        flavor: 'O senhor do código binário do cosmos',
        tables: [2, 3, 4, 5, 6, 7, 8, 10],
        timeLimitMs: 4000,
        questionCount: 15,
        isBoss: true,
      },
    ],
  },
  {
    id: 4,
    name: 'Caverna dos Heróis',
    theme: 'Capas, máscaras e o multiverso geek',
    accentCss: '#facc15',
    levels: [
      {
        id: 'world-4-level-1',
        number: 1,
        name: 'Origem',
        flavor: 'Toda lenda começa com um aquecimento',
        tables: [4, 6, 7, 8, 10],
        timeLimitMs: 5500,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-4-level-2',
        number: 2,
        name: 'Sinal no Céu',
        flavor: 'O chamado heroico — tabuada do 9 estreia',
        tables: [9],
        timeLimitMs: 5500,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-4-level-3',
        number: 3,
        name: 'Patrulha Noturna',
        flavor: 'Mistura o 9 com o que você já manda bem',
        tables: [6, 7, 9],
        timeLimitMs: 5000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-4-level-4',
        number: 4,
        name: 'Liga dos Geeks',
        flavor: 'Reúna os tabuada-mestres — tier alto unido',
        tables: [7, 8, 9],
        timeLimitMs: 5000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-4-level-5',
        number: 5,
        name: 'Capa & Máscara',
        flavor: 'Disfarce não engana — só precisão',
        tables: [6, 8, 9],
        timeLimitMs: 4500,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-4-level-6',
        number: 6,
        name: 'Vilão das Sombras',
        flavor: 'Ataques rápidos exigem reflexo afiado',
        tables: [4, 7, 8, 9],
        timeLimitMs: 4500,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-4-level-7',
        number: 7,
        name: 'Multiverso',
        flavor: 'Realidades cruzadas — toda tabuada de uma vez',
        tables: [3, 6, 7, 8, 9, 10],
        timeLimitMs: 4000,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-4-level-8',
        number: 8,
        name: 'Confronto Final',
        flavor: 'Última prova antes do covil do imperador',
        tables: [2, 3, 4, 6, 7, 8, 9, 10],
        timeLimitMs: 3700,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-4-level-boss',
        number: 9,
        name: 'Vilão Quântico',
        flavor: 'Manipula realidade — só uma resposta certa por dimensão',
        tables: [2, 3, 4, 5, 6, 7, 8, 9, 10],
        timeLimitMs: 3500,
        questionCount: 15,
        isBoss: true,
      },
    ],
  },
  {
    id: 5,
    name: 'Reino RPG',
    theme: 'Magos, dragões e masmorras',
    accentCss: '#ef4444',
    levels: [
      {
        id: 'world-5-level-1',
        number: 1,
        name: 'Taverna do Iniciante',
        flavor: 'Reúna a party — aquecimento antes da jornada',
        tables: [4, 7, 8, 9, 10],
        timeLimitMs: 5000,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-5-level-2',
        number: 2,
        name: 'Pergaminho Arcano',
        flavor: 'Magia rara — tabuada do 11 desperta',
        tables: [11],
        timeLimitMs: 5000,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-5-level-3',
        number: 3,
        name: 'Calabouço dos Goblins',
        flavor: 'Mistura o 11 com o que já manda bem',
        tables: [9, 11],
        timeLimitMs: 4500,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-5-level-4',
        number: 4,
        name: 'Floresta Élfica',
        flavor: 'Precisão de arqueiro com tabuada de tier alto',
        tables: [8, 9, 11],
        timeLimitMs: 4500,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-5-level-5',
        number: 5,
        name: 'Forja Ancestral',
        flavor: 'Bate o ritmo do martelo — tempo aperta',
        tables: [7, 9, 11],
        timeLimitMs: 4000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-5-level-6',
        number: 6,
        name: 'Torre do Necromante',
        flavor: 'Feitiços rápidos — só quem decora sobrevive',
        tables: [6, 8, 9, 11],
        timeLimitMs: 4000,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-5-level-7',
        number: 7,
        name: 'Câmara dos Cristais',
        flavor: 'Cada cristal guarda uma tabuada diferente',
        tables: [4, 6, 7, 8, 9, 11],
        timeLimitMs: 3700,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-5-level-8',
        number: 8,
        name: 'Portão do Castelo',
        flavor: 'Última prova antes do covil do dragão',
        tables: [3, 4, 6, 7, 8, 9, 10, 11],
        timeLimitMs: 3500,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-5-level-boss',
        number: 9,
        name: 'Dragão dos Decimais',
        flavor: 'Cospe contas em fogo — a tabuada toda em fúria',
        tables: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
        timeLimitMs: 3300,
        questionCount: 15,
        isBoss: true,
      },
    ],
  },
  {
    id: 6,
    name: 'Cidade Cyberpunk',
    theme: 'Hackers, neon e o futuro pós-tudo',
    accentCss: '#fbbf24',
    levels: [
      {
        id: 'world-6-level-1',
        number: 1,
        name: 'Login na Matrix',
        flavor: 'Conexão estabelecida — aquecimento na rede',
        tables: [4, 7, 8, 9, 11],
        timeLimitMs: 4500,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-6-level-2',
        number: 2,
        name: 'Vírus Zero',
        flavor: 'Pacote suspeito na entrada — tabuada do 12 desbloqueia',
        tables: [12],
        timeLimitMs: 4500,
        questionCount: 8,
        isBoss: false,
      },
      {
        id: 'world-6-level-3',
        number: 3,
        name: 'Hack do Mainframe',
        flavor: 'Mistura o 12 com o tier alto que você já manda',
        tables: [9, 11, 12],
        timeLimitMs: 4000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-6-level-4',
        number: 4,
        name: 'Beco do Neon',
        flavor: 'Reflexos rápidos no underworld da cidade',
        tables: [8, 11, 12],
        timeLimitMs: 4000,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-6-level-5',
        number: 5,
        name: 'Megacorp',
        flavor: 'Burlar firewall corporativo exige precisão',
        tables: [7, 11, 12],
        timeLimitMs: 3700,
        questionCount: 10,
        isBoss: false,
      },
      {
        id: 'world-6-level-6',
        number: 6,
        name: 'Black Ice',
        flavor: 'Defesas letais — uma resposta errada e adeus',
        tables: [6, 9, 11, 12],
        timeLimitMs: 3700,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-6-level-7',
        number: 7,
        name: 'Cidade Subterrânea',
        flavor: 'Conexões cruzadas — a tabuada inteira em néon',
        tables: [4, 6, 7, 8, 9, 11, 12],
        timeLimitMs: 3500,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-6-level-8',
        number: 8,
        name: 'Última Patch',
        flavor: 'Update final antes do confronto com a IA',
        tables: [2, 3, 4, 6, 7, 8, 9, 10, 11, 12],
        timeLimitMs: 3300,
        questionCount: 12,
        isBoss: false,
      },
      {
        id: 'world-6-level-boss',
        number: 9,
        name: 'A.I. Suprema',
        flavor: 'A inteligência artificial que dominou tudo — só Mestre Geek encara',
        tables: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        timeLimitMs: 3000,
        questionCount: 18,
        isBoss: true,
      },
    ],
  },
];

export function getWorld(worldId: number): World | undefined {
  return WORLDS.find((w) => w.id === worldId);
}

export function getLevel(worldId: number, levelId: string): Level | undefined {
  return getWorld(worldId)?.levels.find((l) => l.id === levelId);
}

import { isDevModeEnabled } from '@/systems/DevMode';

/**
 * Mundo está desbloqueado quando o jogador concluiu **todas** as fases
 * do mundo anterior com pelo menos 2 estrelas. Mundo 1 é sempre aberto.
 *
 * Decisão de design: 2★ exige consistência (sem perder vida) sem chegar
 * ao perfeccionismo do 3★. Equilibra progressão visível com aprendizado
 * sólido antes de avançar de tier.
 *
 * Mundos placeholder (sem fases) nunca destravam o seguinte — não tem o
 * que concluir.
 *
 * Modo dev (senha admin) força true pra testar fases sem grindar.
 */
export function isWorldUnlocked(
  world: World,
  starsPerLevel: Readonly<Record<string, number>>,
): boolean {
  if (isDevModeEnabled()) return true;
  if (world.id === 1) return true;
  const previous = WORLDS.find((w) => w.id === world.id - 1);
  if (!previous || previous.levels.length === 0) return false;
  return previous.levels.every((l) => (starsPerLevel[l.id] ?? 0) >= 2);
}

/**
 * Fase está desbloqueada se o mundo dela está desbloqueado e:
 *   - é a fase 1, OU
 *   - a fase anterior tem >= 1 estrela (já foi concluída).
 *
 * Mantém a progressão linear dentro do mundo sem trancar tudo numa
 * sequência rígida: bastou tirar 1★ pra avançar.
 *
 * Modo dev força true direto.
 */
export function isLevelUnlocked(
  world: World,
  level: Level,
  starsPerLevel: Readonly<Record<string, number>>,
): boolean {
  if (isDevModeEnabled()) return true;
  if (!isWorldUnlocked(world, starsPerLevel)) return false;
  const idx = world.levels.findIndex((l) => l.id === level.id);
  if (idx <= 0) return idx === 0;
  const previous = world.levels[idx - 1]!;
  return (starsPerLevel[previous.id] ?? 0) >= 1;
}
